import type {
  ColDef,
  ColGroupDef,
  Column,
  GridApi,
  IAggFuncParams,
  IRowNode,
  ProcessCellForExportParams,
  ProcessHeaderForExportParams,
  SelectionEventSourceType,
  ValueGetterParams,
} from "ag-grid-community"
import { calculateWeightedAverage } from "./math"
import { filterDefined } from "./tsUtils"

const USER_SELECTION_SOURCES: ReadonlySet<SelectionEventSourceType> = new Set([
  "checkboxSelected",
  "rowClicked",
  "spaceKey",
  "keyboardSelectAll",
  "uiSelectAll",
  "uiSelectAllFiltered",
  "uiSelectAllCurrentPage",
])

export const isUserSelectionSource = (source: SelectionEventSourceType) =>
  USER_SELECTION_SOURCES.has(source)

export type ProcessExportCell = (params: ProcessCellForExportParams) => string

export const AUTO_GROUP_COL_ID = "ag-Grid-AutoColumn"

/** The group column ag-grid adds itself, suffixed per level under `groupDisplayType: "multipleColumns"`. */
export const isAutoGroupColumn = (column: { getColId: () => string }) =>
  column.getColId().startsWith(AUTO_GROUP_COL_ID)

// AG-Grid 35 skips the value-getter on a group row without an aggFunc, while the cell on screen
// computes it from the group node, so run the getter here for the same figure.
export function groupRowExportValue<T>(params: ProcessCellForExportParams<T>): unknown {
  if (params.value != null) return params.value
  const colDef: ColDef<T> = params.column.getColDef()
  const { node } = params
  const aggregated = colDef.aggFunc || colDef.initialAggFunc
  if (!node || aggregated || typeof colDef.valueGetter !== "function") return params.value
  return colDef.valueGetter({
    node,
    data: node.data,
    colDef,
    column: params.column,
    api: params.api,
    context: params.context,
    getValue: (field: string) => params.api.getCellValue({ rowNode: node, colKey: field }),
  } as ValueGetterParams<T>)
}

// Reapplying the formatter wholesale to a grid that sets its own callback would start formatting
// columns it deliberately exports raw (a "1.2k" cell instead of 1234) — those opt in per column.
const usesValueFormatterForExport = (colDef: ColDef, hasCustomCallback: boolean) =>
  hasCustomCallback
    ? colDef.useValueFormatterForExport === true
    : colDef.useValueFormatterForExport !== false

const isValueWrapper = (value: unknown): value is { value: unknown } =>
  !!value && typeof value === "object" && "value" in value

/**
 * The cell value ag-grid should write to a CSV/xlsx export.
 *
 * Cell renderers are never used on export, so a column that renders an object cell value
 * without a `valueFormatter` exports "[object Object]" (HAK-2294) — unwrap the `{ value }`
 * shape our tables use. Supplying `processCellCallback` at all also suppresses ag-grid's own
 * `useValueFormatterForExport`, so reapply that here, minus the numeric-Excel case ag-grid
 * itself skips to keep xlsx cells summable rather than formatted text.
 */
export const makeProcessExportCell = (
  format: "csv" | "excel",
  customCallback: ProcessExportCell | undefined,
): ProcessExportCell => {
  return (params) => {
    const colDef = params.column.getColDef()
    const value = customCallback ? customCallback(params) : params.value

    // xlsx only: `createDateColumn`'s `cellClass: "dateType"` excel style is a DateTime number
    // format, which needs an ISO value rather than the column's humanized valueFormatter text.
    if (format === "excel" && colDef.cellDataType === "date" && value != null) {
      const date = new Date(value as unknown as string)
      if (!isNaN(date.getTime())) {
        return date.toISOString().replace(/\.\d{3}Z$/, "")
      }
    }

    // A leaf's list is that row's own values: write them, not the formatter's "2 collections".
    if (
      Array.isArray(value) &&
      !params.node?.group &&
      colDef.useValueFormatterForExport !== true &&
      value.every((v) => v == null || typeof v !== "object")
    ) {
      return value.filter((v) => v != null && v !== "").join(", ")
    }

    // Formatters expect the primitive: a `{ value }` cell (incoming stock, avg aggregates) would
    // otherwise format as "" (SUP-465).
    const raw = isValueWrapper(params.value) ? params.value.value : params.value

    if (
      colDef.valueFormatter &&
      usesValueFormatterForExport(colDef, !!customCallback) &&
      !(format === "excel" && typeof raw === "number")
    ) {
      return params.formatValue(raw)
    }

    // Cells that hand the renderer a whole object: `value` is our wrapper shape, `current` the
    // capacity-calendar overlay ({ current, snapshot, draftPOQty, … }) — HAK-2553.
    if (value && typeof value === "object") {
      if (isValueWrapper(value)) return value.value as string
      if ("current" in value) return (value as { current: string }).current
    }

    return value
  }
}

/**
 * The header text an export should write: `headerName` plus the `headerSubtitle` the header cell
 * renders under it. AG Grid writes `headerName` alone, which collapses a per-warehouse column
 * family to one repeated heading.
 */
export const exportHeaderWithSubtitle = ({ column }: ProcessHeaderForExportParams): string => {
  const colDef = column.getColDef()
  const name = colDef.headerName ?? column.getColId()
  const subtitle = (colDef.headerComponentParams as { subtitle?: string } | undefined)?.subtitle
  return subtitle ? `${name} (${subtitle})` : name
}

/**
 * An editable column's `editable`, neutralised to `false` unless the user may write. A column
 * that was never editable stays that way.
 *
 * Grid cells are the one mutation-triggering control that no `requires` prop reaches: a
 * read-only user can otherwise type into a cell and only discover the problem when the save
 * fails. Apply it in the colDef pipeline of every grid whose edits reach a mutation, keyed to
 * the tier that mutation's procedure requires (`permissions.canEdit` for `editableProcedure`).
 */
export const clampEditable = (colDef: Pick<ColDef, "editable">, allowed: boolean) =>
  colDef.editable && allowed ? colDef.editable : false

export function isColGroupDef<T>(def: ColDef<T> | ColGroupDef<T>): def is ColGroupDef<T> {
  return "children" in def && Array.isArray(def.children)
}

/** Maps every leaf column through `fn`, keeping the group structure. */
export function mapColDefLeaves<T>(
  defs: (ColDef<T> | ColGroupDef<T>)[],
  fn: (def: ColDef<T>) => ColDef<T>,
): (ColDef<T> | ColGroupDef<T>)[] {
  return defs.map((def) =>
    isColGroupDef(def) ? { ...def, children: mapColDefLeaves(def.children, fn) } : fn(def),
  )
}

/**
 * AG Grid re-applies `pinned` to every column each time `columnDefs` change, so a default pin
 * would override the user's pin on any rerender. `initialPinned` only applies when the column
 * is created (and on reset). `lockPinned` columns keep `pinned`: the user can't move them.
 */
export function pinnedToInitial<T>(def: ColDef<T>): ColDef<T> {
  if (def.pinned === undefined || def.lockPinned) return def
  const { pinned, ...rest } = def
  return { ...rest, initialPinned: pinned ?? undefined }
}

/**
 * Reads filter input for a column whose value is a 0–1 fraction but whose cell reads as a
 * percentage: "0.9" and "90" both mean 90% (anything above 1 in magnitude is divided by 100);
 * a comma is accepted as the decimal separator.
 *
 * Beware when adding columns: AG Grid writes the parsed model value back into the input on
 * every change, so a typed "90" is rewritten to "0.9" mid-keystroke. A column whose users type
 * whole percents should expose whole percents through a filterValueGetter instead.
 */
export const percentageFilterParams = {
  // AG Grid wraps this in its own character class, so it must not carry brackets of its own.
  allowedCharPattern: "\\d.,\\-",
  numberParser: (text: string | null) => {
    if (text == null || text.trim() === "") return null
    const value = Number(text.trim().replace(",", "."))
    if (!Number.isFinite(value)) return null
    return Math.abs(value) > 1 ? value / 100 : value
  },
  numberFormatter: (value: number | null) => (value == null ? null : String(value)),
}

/**
 * Collects leaf-node data so that custom aggFuncs / cellRenderers see every
 * descendant leaf.
 *
 * Uses `allLeafChildren` (all descendants, already flattened) rather than
 * recursing through `childrenAfterFilter`, because with multi-level grouping
 * (e.g. Product Name → Variant) and `groupHideParentOfSingleChild="leafGroupsOnly"`,
 * intermediate leaf-groups can have `childrenAfterFilter = []` in some render
 * states. The previous `??` fallback didn't kick in for empty arrays, so the
 * recursion bottomed out with no leaves and aggregations returned undefined.
 */
export function getFilteredLeafChildren<T>(rowNode: IRowNode<T> | undefined | null): IRowNode<T>[] {
  if (!rowNode) return []
  if (!rowNode.group) return rowNode.data != null ? [rowNode] : []
  return (rowNode.allLeafChildren ?? []).filter((c) => c.data != null)
}

/** Row ids of the leaves these nodes stand for (a group for its filtered leaves), deduped. */
export function leafRowIds<T>(nodes: readonly (IRowNode<T> | null | undefined)[]): string[] {
  return [
    ...new Set(filterDefined(nodes.flatMap((n) => getFilteredLeafChildren(n).map((l) => l.id)))),
  ]
}

/** Leaf row data under a group node, or the row's own data for a leaf (`[]` when absent). */
export function getLeafRows<T>(params: { node?: IRowNode<T> | null; data?: T | null }): T[] {
  if (params.node?.group)
    return filterDefined((params.node.allLeafChildren ?? []).map((n) => n.data))
  return params.data ? [params.data] : []
}

/** The row's variant, read off its first leaf on a group node: a sort runs on the variant group, whose `data` is empty. */
export const leafVariantId = <T extends { variantId: string }>(params: {
  node?: IRowNode<T> | null
  data?: T | null
}): string => getLeafRows(params)[0]?.variantId ?? ""

/**
 * The earliest row model step the columns' new values invalidate. Each step re-runs the later
 * ones, filter before aggregate before sort, so one pass covers them all.
 */
function staleRowModelStep(api: GridApi, columns: readonly Column[]) {
  if (columns.some((column) => api.getColumnFilterModel(column) != null)) return "filter"
  if (columns.some((column) => column.getAggFunc() != null || column.getSort() != null)) {
    return "aggregate"
  }
  return undefined
}

/**
 * Repaints the given columns after data they read outside `rowData` changed. The grid caches
 * values and only expires the cache on a `rowData` change, and renderers may read that data
 * beside `params.value`, so the refresh is forced after the row model re-runs the first step the
 * new values affect.
 */
export function refreshColumnValues(api: GridApi, colIds: readonly string[]) {
  const columns = filterDefined(colIds.map((colId) => api.getColumn(colId) ?? undefined))
  if (columns.length === 0) return
  api.expireValueCache()
  // A hidden column keeps its filter, aggregates and sort order, but has no cells to repaint.
  const step = staleRowModelStep(api, columns)
  if (step) api.refreshClientSideRowModel(step)
  const visibleColIds = columns
    .filter((column) => column.isVisible())
    .map((column) => column.getColId())
  if (visibleColIds.length > 0) repaintColumns(api, visibleColIds)
}

/** The grid's open detail grids. */
export function detailGridApis(api: GridApi): GridApi[] {
  const apis: GridApi[] = []
  api.forEachDetailGridInfo(({ api: detailApi }) => {
    if (detailApi && !detailApi.isDestroyed()) apis.push(detailApi)
  })
  return apis
}

/**
 * Force-repaints the columns but flashes only the cells whose value changed: a forced refresh
 * flashes every cell it touches, so changed values repaint first and the force follows unflashed.
 */
export function repaintColumns(api: GridApi, colIds: readonly string[]) {
  const columns = [...colIds]
  api.refreshCells({ columns })
  api.refreshCells({ columns, force: true, suppressFlash: true })
}

/**
 * Shorthand: returns the `.data` of every filtered leaf child (non-null).
 */
export function getFilteredLeafData<T>(rowNode: IRowNode<T> | undefined | null): T[] {
  return getFilteredLeafChildren(rowNode)
    .map((n) => n.data)
    .filter((d): d is T => d != null)
}

/**
 * Leaf descendants of `rowNode` that are currently in view (with the active filter applied).
 */
export function getVisibleLeafChildren<T>(
  rowNode: IRowNode<T> | undefined | null,
  api: { forEachNodeAfterFilterAndSort: (cb: (node: IRowNode<T>) => void) => void },
): IRowNode<T>[] {
  const allLeaves = getFilteredLeafChildren(rowNode)
  const visibleIds = new Set<string>()
  api.forEachNodeAfterFilterAndSort((node) => {
    if (node.group || node.footer) return
    if (node.id != null) visibleIds.add(node.id)
  })
  return allLeaves.filter((leaf) => leaf.id != null && visibleIds.has(leaf.id))
}

/** Minimal slice of ag-grid's api the displayed-order helpers need, so callers can pass a mock. */
type DisplayedOrderApi<T> = {
  forEachNodeAfterFilterAndSort: (cb: (node: IRowNode<T>) => void) => void
}

/**
 * Leaf rows in the order the user sees them — filter and sort applied, groups and footers skipped.
 *
 * This is the order row-to-row navigation must walk: stepping through `rowData` would jump to rows
 * that are filtered out or sorted elsewhere.
 */
export function getDisplayedLeafNodes<T>(api: DisplayedOrderApi<T>): IRowNode<T>[] {
  const nodes: IRowNode<T>[] = []
  api.forEachNodeAfterFilterAndSort((node) => {
    if (node.group || node.footer || node.data == null) return
    nodes.push(node)
  })
  return nodes
}

/**
 * Steps `delta` rows from `currentId` through displayed order, clamping at both ends rather than
 * wrapping. Returns null when nothing is displayed; falls back to the first row when `currentId`
 * isn't in view (a filter can drop it).
 */
export function stepDisplayedLeafNode<T>(
  api: DisplayedOrderApi<T>,
  currentId: string | null,
  delta: 1 | -1,
  getId: (node: IRowNode<T>) => string | null | undefined,
): IRowNode<T> | null {
  const nodes = getDisplayedLeafNodes(api)
  if (nodes.length === 0) return null
  const currentIndex = currentId == null ? -1 : nodes.findIndex((n) => getId(n) === currentId)
  if (currentIndex === -1) return nodes[0] ?? null
  const nextIndex = Math.min(nodes.length - 1, Math.max(0, currentIndex + delta))
  return nodes[nextIndex] ?? null
}

/**
 * Calculates the weighted average for a given value and weight key from ag-grid aggregation parameters.
 *
 * @param params - ag-Grid aggregation function parameters.
 * @param valueKey - The key of the value property to average.
 * @param weightKey - The key of the weight property.
 * @returns The weighted average as a number, or undefined if no valid values.
 *
 * @example
 * aggFunc: (params) => generateWeightedAverageFromParams(params, "costPrice", "stockLevel")
 */
export function generateWeightedAverageFromParams<T>(
  params: IAggFuncParams<T>,
  valueKey: Extract<keyof T, string>,
  weightKey: Extract<keyof T, string>,
) {
  const children = generateChildrenFromParams(params)
  return calculateWeightedAverage(children, valueKey, weightKey)
}

function generateChildrenFromParams(params: IAggFuncParams) {
  return getFilteredLeafData(params?.rowNode)
}

/**
 * Collapses expanded master-detail rows in one batch. Not `api.collapseAll()`, which would also
 * close row groups, and not per-node `setExpanded`, which is slow at scale.
 */
const sameValueKey = (v: unknown) => (v instanceof Date ? v.getTime() : v)

/**
 * The children's value when they all share it, else null. For per-variant facts (a date, an age)
 * that must not sum: a variant's group keeps its value, so the column sorts, and a mixed total
 * stays blank.
 */
export function sameValueAggFunc(params: IAggFuncParams): unknown {
  const values = params.values.filter((v) => v != null)
  if (values.length === 0) return null
  const first = sameValueKey(values[0])
  return values.every((v) => sameValueKey(v) === first) ? values[0] : null
}

export function collapseMasterRows(api: GridApi | null | undefined) {
  if (!api) return
  let anyCollapsed = false
  api.forEachNode((node) => {
    if (node.expanded && node.master) {
      node.expanded = false
      anyCollapsed = true
    }
  })
  if (anyCollapsed) api.onGroupExpandedOrCollapsed()
}
